/**
 * User Selector Component
 * Multi-select component for selecting push notification target users
 */

import { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { UserContactInfo } from '@/components/common/UserContactInfo';
import { usePushUsers } from '../hooks/usePushNotifications';
import { useI18n } from '@/i18n';

export interface UserSelectorProps {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
}

export function UserSelector({ selectedUserIds, onChange, disabled }: UserSelectorProps) {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const tc = messages.common;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = usePushUsers({
    search: debouncedSearch || undefined,
    has_token: true,
    page,
    limit,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const hasMore = page * limit < total;

  // Check if user is selected
  const isSelected = useCallback(
    (userId: string) => selectedUserIds.includes(userId),
    [selectedUserIds]
  );

  // Toggle user selection
  const toggleUser = useCallback(
    (userId: string) => {
      if (disabled) return;
      if (isSelected(userId)) {
        onChange(selectedUserIds.filter((id) => id !== userId));
      } else {
        onChange([...selectedUserIds, userId]);
      }
    },
    [selectedUserIds, onChange, isSelected, disabled]
  );

  // Select all visible users
  const selectAll = useCallback(() => {
    if (disabled) return;
    const newIds = [...new Set([...selectedUserIds, ...users.map((u) => u.user_id)])];
    onChange(newIds);
  }, [users, selectedUserIds, onChange, disabled]);

  // Deselect all visible users
  const deselectAll = useCallback(() => {
    if (disabled) return;
    const visibleIds = new Set(users.map((u) => u.user_id));
    onChange(selectedUserIds.filter((id) => !visibleIds.has(id)));
  }, [users, selectedUserIds, onChange, disabled]);

  // Clear all selections
  const clearAll = useCallback(() => {
    if (disabled) return;
    onChange([]);
  }, [onChange, disabled]);

  // Handle search with debounce effect
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1); // Reset to first page on search
    },
    []
  );

  // Get display text for platforms
  const getPlatformBadge = (platforms: string) => {
    if (!platforms) return null;
    const parts = platforms.split(', ').filter(Boolean);
    if (parts.length === 0) return null;
    return (
      <span className="inline-flex gap-1 ml-2">
        {parts.includes('ios') && <span className="text-xs">iOS</span>}
        {parts.includes('android') && <span className="text-xs">Android</span>}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <Input
        placeholder={t.userSelector?.searchPlaceholder ?? 'Search by name or ID...'}
        value={search}
        onChange={handleSearchChange}
        disabled={disabled}
      />

      {/* Selection Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {t.userSelector?.selectedCount?.replace('{count}', String(selectedUserIds.length)) ??
            `${selectedUserIds.length} users selected`}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={selectAll}
            disabled={disabled || users.length === 0}
            className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            {t.userSelector?.selectAll ?? 'Select All'}
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={deselectAll}
            disabled={disabled}
            className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            {t.userSelector?.deselectAll ?? 'Deselect All'}
          </button>
          {selectedUserIds.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAll}
                disabled={disabled}
                className="text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {t.userSelector?.clearAll ?? 'Clear'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* User List */}
      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
        {isLoading && (
          <div className="p-4 text-center text-gray-500">
            {tc.loading}
          </div>
        )}

        {error && (
          <div className="p-4 text-center text-red-600">
            {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {!isLoading && !error && users.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            {t.userSelector?.noUsers ?? 'No users found'}
          </div>
        )}

        {!isLoading && !error && users.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {users.map((user) => (
              <li
                key={user.user_id}
                onClick={() => toggleUser(user.user_id)}
                className={`
                  px-4 py-2 flex items-center justify-between cursor-pointer
                  hover:bg-gray-50 transition-colors
                  ${isSelected(user.user_id) ? 'bg-indigo-50' : ''}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected(user.user_id)}
                    onChange={() => toggleUser(user.user_id)}
                    disabled={disabled}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {user.display_name}
                      {getPlatformBadge(user.platforms)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {user.user_id}
                      {user.token_count > 0 && (
                        <span className="ml-2 text-green-600">
                          {user.token_count} {t.userSelector?.devices ?? 'device(s)'}
                        </span>
                      )}
                    </div>
                    <UserContactInfo phone={user.phone_number} email={user.email} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {t.userSelector?.showingCount
              ?.replace('{from}', String((page - 1) * limit + 1))
              .replace('{to}', String(Math.min(page * limit, total)))
              .replace('{total}', String(total)) ??
              `Showing ${(page - 1) * limit + 1}-${Math.min(page * limit, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || disabled}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {tc.previous}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore || disabled}
              className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {tc.next}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
